<script setup lang="ts">
import { computed } from 'vue'

import { useImgSrcSet } from '../../../composables/use-img-src-set'

import type { Brand } from '@xcel-next/core/enums'
import type { SliderGalleryItem } from '../../../utils/paragraph-schema/paragraphs'

const props = withDefaults(defineProps<{
  item: SliderGalleryItem,
  srcSetName?: string,
  brand?: Brand,
  activeSlide?: boolean,
  viewportWidth?: number,
  isPreview?: boolean
}>(), {
  activeSlide: false,
  isPreview: false
})

const { createImgSrc, createImgSrcSetArray, getImgDefinitions } = useImgSrcSet()

// ensure no empty alt/title attributes are rendered
const stripQuotes = (inputString: string | null | undefined): string | undefined =>
  inputString && inputString.length > 0 ? inputString.replace(/['"]+/g, '') : undefined

const setMediaQuery = (viewport: string): string => '(max-width: ' + viewport + 'px)'

const fallbackImage = computed(() => {
  if (!props.item.image) return undefined
  return createImgSrc({
    image: props.item.image,
    brand: props.brand ?? '',
    setName: props.srcSetName,
    type: 'webp'
  }) ?? undefined
})

const imgSrcSetArray = computed(() => {
  if (!props.item.image) return []
  return createImgSrcSetArray(props.item.image, props.brand ?? '', props.srcSetName, 'webp')
})

const viewportSpecificImage = computed(() => {
  const srcSetDefinition = getImgDefinitions(props.brand ?? '', props.srcSetName)
  const matched = srcSetDefinition?.srcSet?.find((entry) => (props.viewportWidth ?? 0) <= Number(entry.viewportWidth))
  return fallbackImage.value?.replace(/w=\d+,/, `w=${matched?.width},`)
})

const imgLoadingValue = computed(() => ((props.isPreview || props.activeSlide) ? 'eager' : 'lazy'))

const imgAttributes = computed(() => ({
  width: props.item.image?.width,
  height: props.item.image?.height,
  title: stripQuotes(props.item.image?.imageTitle),
  alt: stripQuotes(props.item.image?.imageAltText)
}))
</script>

<template>
  <picture v-if="isPreview || activeSlide">
    <source v-for="(singleSrc, id) in imgSrcSetArray"
            :key="id + 'webp'"
            type="image/webp"
            :media="setMediaQuery(singleSrc.viewportWidth)"
            :srcset="singleSrc.src">
    <img v-bind="imgAttributes"
         :src="fallbackImage"
         :loading="imgLoadingValue">
  </picture>
  <picture v-else>
    <img v-bind="imgAttributes"
         :data-src="fallbackImage"
         :data-flickity-lazyload="viewportSpecificImage">
  </picture>
</template>
